/* Prüf-Dialog: Map-Typ, Prüfergebnisse, Engine-Limits und die Leak-Anzeige (bzw. Zusammenfassung einer geladenen BSP). */
import { useEffect, useRef } from 'react';
import Cmd from '../../model/cmds.ts';
import { applyFix } from '../../model/checkfix.ts';
import { CHECK_INFO } from '../../model/checkinfo.ts';
import { FIXES } from '../../model/fixes.ts';
import { applyFixes, checkDoc, clipLimits } from '../../services/check.ts';
import { scoreOf } from '../../services/score.ts';
import { reachBsp } from '../../services/reach.ts';
import type { ReachReport } from '../../services/reach.ts';
import Core from '../../core.ts';
import Leak from '../../formats/leak.ts';
import Limits from '../../analysis/limits.ts';
import type { Level } from '../../analysis/limits.ts';
import type { BspFile } from '../../formats/bsp-parse.ts';
import type { CheckFix, CheckItem, Doc, Entity, Sel, Vec3 } from '../../model/types.ts';
import I18n from '../../i18n.ts';
import { Dialog, DialogBody } from '../dialog.tsx';
import { useT } from '../hooks.ts';
import { createNotifier, useNotifier } from '../notifier.ts';
import type { Notifier } from '../notifier.ts';

export interface CheckApp {
  doc: Doc;
  bsp: { data: BspFile; orig?: { ents: Entity[] }; raw?: ArrayBuffer | Uint8Array } | null;
  wads: { count(): number; has(n: string): boolean; size(n: string): { w: number; h: number } | null };
  leak: { pts: Vec3[]; name: string } | null;
  status(msg: string): void;
  setSel(sel: Sel): void;
  hiddenSet(): Set<string>;
  commit(doc: Doc, label: string): void;
  fit(selection: boolean): void;
  clearLeak(): void;
  /** Markierungen aus Befunden (rote Flächen in den Ansichten); fehlt in Tests und beim BSP */
  marks?: Vec3[][] | null;
  showMarks?(polys: Vec3[][]): void;
  clearMarks?(): void;
  focusPoint(p: Vec3, r?: number): void;
}
/** Zustand, den die Seite über das Neuzeichnen hinaus behält: letztes Prüfergebnis (null = neu prüfen) und der gewählte Leak-Punkt */
/** fixPreview: Ergebnis der Vorschau von „Alle lösbaren beheben“ (null: keine angezeigt) */
export interface CheckShared { checkRes: CheckItem[] | null; leakIdx: number; fixPreview?: { id: string; fix: CheckFix; count: number }[] | null; reach?: ReachReport | null }

const fmt = (n: number): string => Math.round(n).toLocaleString(I18n.lang === 'de' ? 'de-DE' : 'en-US');

function LimBar(props: { ratio: number; hi?: number; level: Level }) {
  return (
    <div className={'limbar ' + props.level}>
      {props.hi ? <i className="band" style={{ width: Math.min(100, props.hi * 100) + '%' }} /> : null}
      <i className="fill" style={{ width: Math.min(100, props.ratio * 100) + '%' }} />
    </div>
  );
}

/** Erreichbarkeit der geladenen BSP (derselbe Service wie `xf reach`); Ergebnis bleibt im Dialogzustand */
function ReachBox(props: { app: CheckApp; shared: CheckShared; refresh: () => void }) {
  const t = useT();
  const { app, shared } = props;
  if (!app.bsp || !app.bsp.raw) return null;
  const run = () => {
    try { shared.reach = reachBsp(app.bsp!.raw as ArrayBuffer | Uint8Array); } catch (err) { shared.reach = null; app.status(t('chk.failed', { msg: (err as Error).message })); }
    props.refresh();
  };
  return (
    <>
      <h3>{t('chk.reach')}</h3>
      <div className="row"><button className="btn sm" title={t('chk.reach_tip')} onClick={run}>{t('chk.reach_run')}</button></div>
      {shared.reach && <ul className="small">{shared.reach.items.map((i) => <li key={i.id} className={i.level}>{i.text}</li>)}</ul>}
    </>
  );
}

function BspInfo({ app }: { app: CheckApp }) {
  const t = useT();
  if (!app.bsp) return null;
  const b = app.bsp.data, mk = Core.mapKind(app.bsp.orig ? app.bsp.orig.ents : []);
  return (
    <>
      <h3>{t('chk.bsp_file')}</h3>
      <p className="small">{t('chk.map_type', { label: mk.label, why: mk.why })}</p>
      <p className="small">{t('chk.bsp_summary', { faces: b.faces.length, models: b.models.length, brushents: b.models.length - 1, tex: b.textures.length, emb: b.textures.filter((x) => x.rgba).length, kb: Math.round(b.lighting.length / 1024) })}</p>
    </>
  );
}

function LimitsBox({ app }: { app: CheckApp }) {
  const t = useT();
  if (app.bsp) {
    const rows = Limits.bspRows(app.bsp.data, 9);
    return (
      <div className="limits">
        <h3>{t('lim.title')}</h3>
        <p className="small">{t('lim.bsp_note')}</p>
        {rows.map((r) => (
          <div key={r.key} className="limrow">
            <span className="limname">{r.label}</span>
            <span className={'limnum ' + r.level}>{fmt(r.used) + ' / ' + fmt(r.max) + ' · ' + Math.round(r.ratio * 100) + ' %'}</span>
            <LimBar ratio={r.ratio} level={r.level} />
          </div>
        ))}
        <p className="small">{t('lim.soft_note')}</p>
      </div>
    );
  }
  let c, e;
  try { c = Limits.classify(app.doc); e = Limits.estimate(c); } catch (err) {
    return <div className="limits"><h3>{t('lim.title')}</h3><p className="small">{t('lim.estimate_fail', { msg: (err as Error).message })}</p></div>;
  }
  const lv = Limits.level(e), ne = app.doc.ents.length, nl = Limits.edictLevel(ne);
  const row = (name: string, cnt: number, cost: number, tip: string) => (
    <div className="limrow sub"><span className="limname">{name}</span><span className="limnum">{t('lim.row', { count: fmt(cnt), cost: fmt(cost) })}</span><span className="small">{tip}</span></div>
  );
  return (
    <div className="limits">
      <h3>{t('lim.title')}</h3>
      <p className="small">{t('lim.clip_note')}</p>
      <div className="limrow">
        <span className="limname">{t('lim.clipnodes')}</span>
        <span className={'limnum ' + lv}>{t('lim.clip_range', { est: fmt(e.est), lo: fmt(e.lo), hi: fmt(e.hi), max: fmt(e.max) })}</span>
        <LimBar ratio={e.est / e.max} hi={e.hi / e.max} level={lv} />
      </div>
      <ul className="checks"><li className={lv}><span className="pill">{t('lim.pill_' + lv)}</span><span>{t('lim.msg_' + lv)}</span></li></ul>
      {row(t('lim.ax'), c.ax, e.parts.ax, t('lim.ax_tip'))}
      {row(t('lim.flat'), c.flat, e.parts.flat, t('lim.flat_tip'))}
      {row(t('lim.steep'), c.steep, e.parts.steep, t('lim.steep_tip'))}
      {row(t('lim.iso'), c.iso, e.parts.iso, t('lim.iso_tip'))}
      {c.iso > 0 && (
        <div className="row">
          <button className="btn sm" title={t('lim.select_expensive_tip')} onClick={() => {
            const ns = Cmd.emptySel(); c.isoList.forEach((x) => ns.b.add(x.id)); app.setSel(ns);
            app.status(t('lim.selected_iso', { count: c.isoList.length }));
          }}>{t('lim.select_expensive')}</button>
        </div>
      )}
      <div className="limrow">
        <span className="limname">{t('find.entities')}</span>
        <span className={'limnum ' + nl}>{t('lim.edicts', { n: fmt(ne) })}</span>
        <LimBar ratio={ne / 900} level={nl} />
      </div>
    </div>
  );
}

function LeakBox(props: { app: CheckApp; shared: CheckShared; refresh: () => void }) {
  const t = useT();
  const { app, shared } = props, L = app.leak;
  const loadFile = () => document.getElementById('f-map')?.click();
  if (!L) {
    return (
      <div id="leak-box">
        <h3>{t('pn.leak')}</h3>
        <div className="row"><button className="btn sm primary" title={t('pn.load_pts_tip')} onClick={loadFile}>{t('pn.load_pts')}</button></div>
        <p className="small">{t('pn.leak_note')}</p>
      </div>
    );
  }
  const pts = L.pts, a = pts[0], z = pts[pts.length - 1], ne = Leak.nearestEntity(app.doc, a);
  const f = (p: Vec3) => p.map((x) => Math.round(x)).join(' ');
  const idx = Math.min(shared.leakIdx, pts.length - 1);
  const go = (i: number) => { shared.leakIdx = Math.max(0, Math.min(pts.length - 1, i)); app.focusPoint(pts[shared.leakIdx], 128); props.refresh(); };
  return (
    <div id="leak-box">
      <h3>{t('pn.leak')}</h3>
      <div className="row">
        <button className="btn sm" title={t('pn.load_pts_tip')} onClick={loadFile}>{t('pn.load_pts')}</button>
        <button className="btn sm" onClick={() => app.clearLeak()}>{t('pn.remove')}</button>
      </div>
      <p className="small">{t('pn.leak_summary', { name: L.name, count: pts.length, len: Math.round(Leak.length(pts)) })}</p>
      <ul className="checks">
        <li className="err"><span className="pill">{t('pn.start')}</span><span>{f(a) + (ne ? ' · ' + t('pn.nearest_ent', { cls: ne.ent.kv.classname, dist: Math.round(ne.dist) }) : '')}</span></li>
        <li className="info"><span className="pill">{t('pn.end')}</span><span>{f(z) + ' · ' + t('pn.in_void')}</span></li>
      </ul>
      <div className="row">
        <button className="btn sm" onClick={() => go(0)}>{t('pn.to_start')}</button>
        <button className="btn sm" onClick={() => go(pts.length - 1)}>{t('pn.to_end')}</button>
        <button className="btn sm" onClick={() => go(idx - 1)}>◀</button>
        <button className="btn sm" onClick={() => go(idx + 1)}>▶</button>
        <span className="small">{t('pn.leak_point', { n: idx + 1, total: pts.length, pos: f(pts[idx]) })}</span>
      </div>
      <p className="small">{t('pn.leak_help')}</p>
    </div>
  );
}

/** Wählt die vom Befund betroffenen Objekte aus (ausgeblendete bleiben außen vor); false, wenn nichts auswählbar war */
function selectItems(app: CheckApp, items: CheckItem[]): number {
  const hid = app.hiddenSet(), ns = Cmd.emptySel();
  items.forEach((it) => {
    if (!it.sel) return;
    it.sel.b.forEach((id) => { if (!hid.has(id)) ns.b.add(id); });
    it.sel.e.forEach((id) => { if (!hid.has(id)) ns.e.add(id); });
  });
  if (Cmd.isEmpty(ns)) return 0;
  app.setSel(ns); app.fit(true);
  return ns.b.size + ns.e.size;
}
const fixable = (r: CheckItem): boolean => !!r.fix && !!r.sel && (r.sel.b.length > 0 || r.sel.e.length > 0);
const selectable = (r: CheckItem): boolean => (r.level === 'warn' || r.level === 'err') && !!r.sel && (r.sel.b.length > 0 || r.sel.e.length > 0);

/** Bewertung der Karte (dieselbe Formel wie `xf score`, services/score.ts) */
function ScoreLine(props: { items: CheckItem[]; app: CheckApp }) {
  const t = useT();
  const s = scoreOf({ items: props.items, clip: clipLimits(props.app.doc) });
  return (
    <p className="small" title={t('chk.score_tip')}>
      <b>{t('chk.score', { total: s.total })}</b>{' ' + t('chk.score_parts', { validity: s.parts.validity, performance: s.parts.performance, limits: s.parts.limits })}
      {s.gates.length > 0 && <>{' · '}<b className="err">{t('chk.score_gates', { gates: s.gates.join(', ') })}</b></>}
    </p>
  );
}

function ChecksList(props: { items: CheckItem[]; names: Record<string, string>; pick: (items: CheckItem[]) => void; fix: (r: CheckItem) => void; show?: (r: CheckItem) => void; hide?: () => void; shown?: boolean }) {
  const t = useT();
  const sel = props.items.filter(selectable);
  return (
    <>
      {props.shown && props.hide && <div className="row"><button className="btn sm" onClick={props.hide}>{t('chk.hide_marks')}</button></div>}
      {sel.length > 1 && <div className="row"><button className="btn sm" title={t('chk.select_all_tip')} onClick={() => props.pick(sel)}>{t('chk.select_all')}</button></div>}
      <ul className="checks">
        {props.items.map((r, i) => (
          <li key={i} className={r.level}>
            <span className="pill">{props.names[r.level] || r.level}</span>
            <span>{r.text}{r.id && CHECK_INFO[r.id] && <> <span className="info-i" tabIndex={0} role="img" aria-label={t(CHECK_INFO[r.id])} title={t(CHECK_INFO[r.id])}>i</span></>}{r.marks && r.marks.length > 0 && props.show && <> <button className="btn sm" title={t('chk.show_tip')} onClick={() => props.show && props.show(r)}>{t('chk.show')}</button></>}{selectable(r) && <> <button className="btn sm" title={t('chk.select_tip')} onClick={() => props.pick([r])}>{t('chk.select')}</button></>}{fixable(r) && <> <button className="btn sm" title={t('chk.fix_tip')} onClick={() => props.fix(r)}>{t(FIXES[r.fix as CheckFix].label)}</button> <span className="info-i" tabIndex={0} role="img" aria-label={t(FIXES[r.fix as CheckFix].info)} title={t(FIXES[r.fix as CheckFix].info)}>i</span></>}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function CheckPanel(props: { app: CheckApp; shared: CheckShared; notifier: Notifier; onClose?: () => void }) {
  const t = useT();
  useNotifier(props.notifier);
  const { app, shared, notifier } = props;
  /** Löst den Befund von Hand aus (Strg+Z macht es rückgängig) und prüft danach neu */
  const fix = (r: CheckItem) => {
    const res = applyFix(app.doc, r.fix as CheckFix, r.sel || { b: [], e: [] });
    if (!res.count) { app.status(t('chk.fix_none')); return; }
    if (app.clearMarks) app.clearMarks();
    app.commit(res.doc, t(FIXES[r.fix as CheckFix].cmd));
    app.status(t('chk.fixed', { count: res.count }));
    shared.checkRes = null; notifier.notify();
  };
  const hasTex = () => (app.wads.count() ? (n: string) => app.wads.has(n) : undefined);
  const texSize = () => (app.wads.count() ? (n: string) => app.wads.size(n) : undefined);
  /** Löst alle automatisch lösbaren Befunde mit demselben Service wie `xf fix` (ein Undo-Schritt); Lösungen mit Spielwirkung (noclip) bleiben der Schaltfläche am Befund vorbehalten */
  const previewAll = () => {
    const res = applyFixes(app.doc, undefined, { hasTex: hasTex(), texSize: texSize(), skipLeak: true });
    shared.fixPreview = res.applied.map((x) => ({ id: x.id, fix: x.fix as CheckFix, count: x.count }));
    if (!res.applied.length) app.status(t('chk.fix_none'));
    notifier.notify();
  };
  const fixAll = () => {
    const res = applyFixes(app.doc, undefined, { hasTex: hasTex(), texSize: texSize(), skipLeak: true });
    if (!res.applied.length) { app.status(t('chk.fix_none')); return; }
    if (app.clearMarks) app.clearMarks();
    app.commit(res.doc, t('chk.fix_all'));
    app.status(t('chk.fixed_all', { count: res.applied.reduce((n, x) => n + x.count, 0), fixes: res.applied.map((x) => x.id).join(', ') }));
    shared.checkRes = null; shared.fixPreview = null; notifier.notify();
  };
  const show = (r: CheckItem) => {
    if (!r.marks || !app.showMarks) return;
    app.showMarks(r.marks);
    app.status(t('chk.marks_shown', { count: r.marks.length }));
    notifier.notify();
  };
  const hide = () => { if (app.clearMarks) app.clearMarks(); app.status(t('chk.marks_hidden')); notifier.notify(); };
  const pick = (items: CheckItem[]) => {
    const n = selectItems(app, items);
    app.status(n ? t('chk.selected', { count: n }) : t('chk.nothing_selectable'));
    if (n && props.onClose) props.onClose();
  };
  const running = useRef(false);

  const run = () => {
    if (running.current) return;
    running.current = true;
    app.status(t('chk.checking'));
    setTimeout(() => {
      try { if (app.clearMarks) app.clearMarks(); shared.fixPreview = null; shared.checkRes = checkDoc(app.doc, { hasTex: hasTex(), texSize: texSize() }).items; }
      catch (err) { shared.checkRes = [{ level: 'err', text: t('chk.failed', { msg: (err as Error).message }) }]; }
      running.current = false;
      app.status(t('chk.done')); notifier.notify();
    }, 20);
  };
  // Ohne Ergebnis (neue Karte, Tab neu geöffnet) wird sofort geprüft
  useEffect(() => { if (!app.bsp && shared.checkRes === null) run(); });

  if (app.bsp) return <><BspInfo app={app} /><ReachBox app={app} shared={shared} refresh={notifier.notify} /><LimitsBox app={app} /></>;
  const mk = Core.mapKind(app.doc.ents);
  const names: Record<string, string> = { ok: t('chk.l_ok'), warn: t('chk.l_warn'), err: t('chk.l_err'), info: t('chk.l_info') };
  return (
    <>
      <p className="small"><b>{t('chk.map_type_short', { label: mk.label })}</b>{' ' + t('chk.detected', { why: mk.why })}</p>
      <div className="row"><button className="btn sm primary" onClick={run}>{t('chk.run')}</button>{shared.checkRes && shared.checkRes.some((r) => r.fix && FIXES[r.fix].auto && r.sel && (r.sel.b.length || r.sel.e.length)) && <><button className="btn sm" title={t('chk.fix_preview_tip')} onClick={previewAll}>{t('chk.fix_preview')}</button><button className="btn sm" title={t('chk.fix_all_tip')} onClick={fixAll}>{t('chk.fix_all')}</button></>}</div>
      {shared.fixPreview && shared.fixPreview.length > 0 && <ul className="small fixpreview">{shared.fixPreview.map((x) => <li key={x.id}><code>{x.id}</code> {t(FIXES[x.fix].label)}: {t('chk.fix_preview_count', { count: x.count })}</li>)}</ul>}
      <p className="small">{t('chk.note')}</p>
      <LimitsBox app={app} />
      <LeakBox app={app} shared={shared} refresh={notifier.notify} />
      {shared.checkRes === null
        ? <p className="small">{t('chk.checking')}</p>
        : <><ScoreLine items={shared.checkRes} app={app} /><ChecksList items={shared.checkRes} names={names} pick={pick} fix={fix} show={show} hide={hide} shown={!!(app.marks && app.marks.length)} /></>}
    </>
  );
}

/** Notifier des offenen Dialogs, damit z. B. ein geladener Leak den Inhalt aktualisiert */
let live: Notifier | null = null;
export const refreshCheckDialog = (): void => { if (live) live.notify(); };

export function CheckDialog(props: { app: CheckApp; shared: CheckShared; onClose: () => void }) {
  const t = useT();
  const notifier = useRef<Notifier | null>(null);
  if (!notifier.current) notifier.current = createNotifier();
  useEffect(() => { live = notifier.current; return () => { if (live === notifier.current) live = null; }; }, []);
  return (
    <Dialog title={t('app.tab.check')} onClose={props.onClose}>
      <DialogBody><CheckPanel app={props.app} shared={props.shared} notifier={notifier.current} onClose={props.onClose} /></DialogBody>
    </Dialog>
  );
}
